import React from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { SearchAIAnalyticsContextLocation } from '../../ai-answer-dialog/analytics';
import {
	type NLPSearchWhoResultCollaboratorDataType,
	type NLPSearchWorkResultRecentActivityDataType,
} from '../../ai-answer-dialog/types';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import { messages } from '../../messages';
import { onKnowledgeCardLinkClick } from '../analytics';
import { KnowledgeCardActivityRow } from '../common/knowledge-card-activity-row';
import {
	type ActivityColumnConfig,
	KnowledgeCardBody,
	type UserColumnConfig,
} from '../common/knowledge-card-body';
import { KnowledgeCardUserRow } from '../common/knowledge-card-user-row';
import { KnowledgeCardsIntentType } from '../types';
import { useTeamsAppLinks } from '../use-teams-app-links';

type KnowledgeCardsBodyProps = {
	currentUserId: string;
	recommendedUsers: NLPSearchWhoResultCollaboratorDataType[];
	recentActivity: NLPSearchWorkResultRecentActivityDataType[];
	profileUrl?: string;
	viewAllUrl?: string;
};

export const PersonCardBody: React.MemoExoticComponent<
	({
		currentUserId,
		recommendedUsers,
		recentActivity,
		profileUrl,
		viewAllUrl,
	}: KnowledgeCardsBodyProps) => React.JSX.Element
> = React.memo(
	({
		currentUserId,
		recommendedUsers,
		recentActivity,
		profileUrl,
		viewAllUrl,
	}: KnowledgeCardsBodyProps): React.JSX.Element => {
		const { commonAttributes } = useAIAnswerContext();
		const [fireAnalyticsEvent] = useAnalytics();
		const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
		const { formatMessage } = useIntl();
		const { viewAllLink, viewAllOnNavigate, worksWithHref, worksWithOnNavigate, profileHref } =
			useTeamsAppLinks({
				type: KnowledgeCardsIntentType.PERSON,
				currentUserId,
				viewAllUrl,
				cloudId: commonAttributes.cloudId,
			});

		const handleRecentWorkViewAllClick = (e: React.MouseEvent) => {
			const isAuxClick = 'button' in e && e.button === 1;
			const isContextMenu = 'button' in e && e.button === 2;
			if ((isAuxClick || isContextMenu) && !SAINFeatureControl.isNonPrimaryClickTrackingEnabled()) {
				return;
			}
			fireAnalyticsEvent(
				onKnowledgeCardLinkClick({
					...commonAttributes,
					location: 'recent-work-view-all',
					isAuxClick,
					isContextMenu,
				}),
			);
			fireSAINUsageEvent({ metCriteria: SainAction.RECENT_WORK_CLICKED, commonAttributes });
			if (!isAuxClick && !isContextMenu && fg('platform-adopt-teams-nav-config')) {
				viewAllOnNavigate(e);
			}
		};

		const handleCollaboratorsViewAllClick = (e: React.MouseEvent) => {
			const isAuxClick = 'button' in e && e.button === 1;
			const isContextMenu = 'button' in e && e.button === 2;
			if ((isAuxClick || isContextMenu) && !SAINFeatureControl.isNonPrimaryClickTrackingEnabled()) {
				return;
			}
			fireAnalyticsEvent(
				onKnowledgeCardLinkClick({
					...commonAttributes,
					location: 'reporting-lines-link',
					isAuxClick,
					isContextMenu,
				}),
			);
			fireSAINUsageEvent({ metCriteria: SainAction.REPORTING_LINES_CLICKED, commonAttributes });
			if (!isAuxClick && !isContextMenu && fg('platform-adopt-teams-nav-config')) {
				worksWithOnNavigate(e);
			}
		};

		const columnsConfig = [
			// Recent Activity Column
			...(recentActivity.length > 0
				? [
						{
							viewAllLinkOnClick: handleRecentWorkViewAllClick,
							viewAllLinkHref: viewAllLink,
							data: recentActivity,
							component: KnowledgeCardActivityRow,
							header: formatMessage(messages.knowledge_cards_recent_work_subheader),
							onClickCallback: (
								position: number,
								shownAfterCardBodyExpanded: boolean,
								opts?: { isAuxClick?: boolean; isContextMenu?: boolean },
							) => {
								if (
									(opts?.isAuxClick || opts?.isContextMenu) &&
									!SAINFeatureControl.isNonPrimaryClickTrackingEnabled()
								) {
									return;
								}
								fireAnalyticsEvent(
									onKnowledgeCardLinkClick({
										...commonAttributes,
										location: 'recent-work-item',
										position,
										shownAfterCardBodyExpanded,
										isAuxClick: opts?.isAuxClick,
										isContextMenu: opts?.isContextMenu,
									}),
								);
								fireSAINUsageEvent({
									metCriteria: SainAction.RECENT_WORK_CLICKED,
									commonAttributes,
								});
							},
							searchAIAnalyticsContextLocation: SearchAIAnalyticsContextLocation.SAIN_PERSON_CARD,
						} as unknown as ActivityColumnConfig,
					]
				: []),
			// Recommended Users Column
			...(recommendedUsers.length > 0
				? [
						{
							viewAllLinkOnClick: handleCollaboratorsViewAllClick,
							viewAllLinkHref: fg('platform-adopt-teams-nav-config')
								? worksWithHref
								: `https://hello.atlassian.net/people/${currentUserId || ''}#workswith`,
							data: recommendedUsers,
							component: KnowledgeCardUserRow,
							onClickCallback: (
								position: number,
								shownAfterCardBodyExpanded: boolean,
								opts?: { isAuxClick?: boolean; isContextMenu?: boolean },
							) => {
								if (
									(opts?.isAuxClick || opts?.isContextMenu) &&
									!SAINFeatureControl.isNonPrimaryClickTrackingEnabled()
								) {
									return;
								}
								fireAnalyticsEvent(
									onKnowledgeCardLinkClick({
										...commonAttributes,
										location: 'user-list-item',
										position,
										shownAfterCardBodyExpanded,
										isAuxClick: opts?.isAuxClick,
										isContextMenu: opts?.isContextMenu,
									}),
								);
								fireSAINUsageEvent({
									metCriteria: SainAction.COLLABORATOR_CLICKED,
									commonAttributes,
								});
							},
							header: formatMessage(messages.knowledge_cards_collaborators_subheader),
						} as unknown as UserColumnConfig,
					]
				: []),
		];

		return (
			<KnowledgeCardBody
				columnsConfig={columnsConfig}
				profileUrl={fg('platform-adopt-teams-nav-config') ? profileHref : profileUrl}
				footerButtonText={formatMessage(messages.ai_answer_who_questions_view_profile)}
				footerButtonAction={SainAction.VIEW_PROFILE_CLICKED}
				testId="person-knowledge-card-body"
			/>
		);
	},
);
